import {
  BulbOutlined,
  IdcardOutlined,
  MessageOutlined,
  NodeIndexOutlined,
  ThunderboltOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Button, Divider, Select, Tag, Typography, Spin, message, Tooltip, Space } from 'antd';
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import Card from '../../Card';
import useAPIQuery from '../../api/useAPIQuery';
import ErrorResult from '../../components/ErrorResult';
import Form from '../../components/Form';
import MultilineParagraph from '../../components/MultilineParagraph';
import PageContent from '../../components/PageContent';
import useDiscardChangesModal from '../../hooks/useDiscardChangesModal';
import useProfileMutation from '../../hooks/useProfileMutation';
import useTypedNavigate from '../../hooks/useTypedNavigate';
import useTypedParams from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import { JobPostingId } from '../../types';
import { getFrontendManualTranslations } from '../../utils';

export const profileKeys = [
  'Arquétipo Operador Comercial',
  'Arquétipo Comercial Operacional',
  'Arquétipo Operador de Atendimento',
  'Arquétipo Atendimento Operacional',
  'Arquétipo Auxiliar Administrativo',
  'Arquétipo Operador Industrial',
  'Arquétipo Operador Logístico',
  'Arquétipo Auxiliar Logístico',
  'Arquétipo Supervisor Operacional',
  'Arquétipo Generalista',
  'Arquétipo Analítico',
  'Arquétipo Vendas',
  'Arquétipo Atendimento',
];

interface ProfileFormValues {
  functionName: string;
  functionId: number;
}

const ProfileForm = () => {
  const { t } = useTranslation();

  const { tenant, id } = useTypedParams({ tenant: String, id: JobPostingId });
  const navigate = useTypedNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm();
  const functionId = Form.useWatch('functionId', form);
  const [leavePage, setLeavePage] = useState<boolean>(false);
  const [setIsDirty] = useDiscardChangesModal(form);

  const queryClient = useQueryClient();

  const jobPosting = useAPIQuery(`/job-postings/${id}/`);

  const recruitmentProcesses = useAPIQuery([
    '/recruitment-processes/',
    {
      params: {
        job_posting: id,
      },
    },
  ]);

  const functions = useAPIQuery(['/mindsight/ats-integrations/functions/']);

  const mutation = useProfileMutation({
    onSuccess: () => {
      setIsDirty(false);
      message.success(t('Vaga atualizada'));

      if (searchParams.get('new')) {
        return navigate({
          pathname: '/:tenant/opportunities/:id/change/description/',
          params: { tenant, id, new: 1 },
        });
      } else if (leavePage) {
        navigate({ pathname: '/:tenant/opportunities/:id/', params: { tenant, id } });
      }
    },
    onSettled: async () => {
      await queryClient.invalidateQueries('/job-postings/');
    },
  });

  const functionName = functions.data?.results.find((fn) => fn.id === functionId)?.name;
  const config = useAPIQuery('/configs/tenant-configurations/');

  const descriptions: { [profile: string]: string } = {
    'Arquétipo Operador Comercial': t(
      'Algoritmo que identifica indivíduos que tem match com funções de vendas mais voltada para poucas interações com clientes, como varejo, vendedores de porta e lojas em geral. Não é o ideal para vagas de comercial mais consultivas, ou seja, com maior quantidade de interações, como vendas de sistemas ou B2B, para este caso é mais recomendado o arquétipo ANALISTA COMERCIAL.'
    ) as string,
    'Arquétipo Comercial Operacional': t(
      'Algoritmo que identifica indivíduos que tem match com funções de vendas mais voltada para poucas interações com clientes, como varejo, vendedores de porta e lojas em geral. Não é o ideal para vagas de comercial mais consultivas, ou seja, com maior quantidade de interações, como vendas de sistemas ou B2B, para este caso é mais recomendado o arquétipo ANALISTA COMERCIAL.'
    ) as string,
    'Arquétipo Operador de Atendimento': t(
      'Algoritmo que identifica indivíduos que tem match com funções que lidam com atendimento ao público ou com clientes de uma forma geral, como atendimento de telemarketing, recepcionistas, secretárias.'
    ) as string,
    'Arquétipo Atendimento Operacional': t(
      'Algoritmo que identifica indivíduos que tem match com funções que lidam com atendimento ao público ou com clientes de uma forma geral, como atendimento de telemarketing, recepcionistas, secretárias.'
    ) as string,
    'Arquétipo Auxiliar Administrativo': t(
      'Algoritmo que identifica indivíduos que tem match com funções de escritório que envolvem, em geral, pouca análise de informações e mais garantia de execução de processos pré-definidos, como verificar entrada e saídas de correspondências, receber e enviar documentos, por exemplo. Não é recomendado para funções de analista, onde há expectativa de produção de conteúdos, para este caso utilize a função GENERALISTA.'
    ) as string,
    'Arquétipo Operador Industrial': t(
      'Algoritmo que identifica indivíduos que tem match com funções de operação de máquinas em indústrias em geral onde há pouco contato com pessoas e envolve mais atividades manuais predeterminadas.'
    ) as string,
    'Arquétipo Operador Logístico': t(
      'Algoritmo que identifica indivíduos que tem match com funções de transporte de objetos e cargas, tanto dentro de uma instalação como estoquista, como fretes de cargas em caminhões e motocicletas. Envolvem pouca interação.'
    ) as string,
    'Arquétipo Auxiliar Logístico': t(
      'Algoritmo que identifica indivíduos que tem match com funções de transporte de objetos e cargas, tanto dentro de uma instalação como estoquista, como fretes de cargas em caminhões e motocicletas. Envolvem pouca interação.'
    ) as string,
    'Arquétipo Supervisor Operacional': t(
      'Algoritmo que identifica indivíduos que tem match com funções de baixa liderança de funções operacionais, que normalmente envolve mais garantir um processo predeterminado do que pensar em diretrizes e políticas; envolve mais gerenciamento de processo do que gerenciamento de pessoas.'
    ) as string,
    'Arquétipo Generalista': t(
      'Algoritmo que identifica pessoas que tem um pouco de cada campo de inteligência. Estes indivíduos possuem alta capacidade de análise e processamento de informação para gerar conclusões lógicas e bem fundamentadas e formular estratégias e dar visão do seu trabalho para a equipe e outros stakeholders. Este algoritmo identifica pessoas com alto nível de automotivação na busca por padrões de excelência em sua atuação e,  geralmente, toma a frente da situação e não esperam a intervenção de terceiros para iniciar ações que gerem impacto. Elas conseguem lidar facilmente com ambiguidade e conseguem interagir e criar relacionamento com pessoas diferentes.\nCargos e Funções relacionadas: Pode ser utilizada para posições que requerem um alto grau de relacionamento e exigem flexibilidade de pensamento para a tomada  de decisão como, por exemplo, cargos estratégicos, de média e alta gestão e programas de estágio e trainee.'
    ) as string,
    'Arquétipo Analítico': t(
      'Algoritmo que identifica indivíduos com alta capacidade para analisar informações, números, dados, etc.; processar estas informações de maneira lógica e chegar a conclusões fundamentadas. Indivíduos com estas características conseguem facilmente formular conceitos e estratégias, determinar problemas e lidar com abstrações. Pessoas com match alto neste algoritmo possuem a capacidade de reflexão, autoanálise e observação dos seus estados e pontos fortes e fracos, o que acelera o aprendizado e o desenvolvimento de soluções para desafios na organização. Geralmente  elas  são  pouco influenciadas pelo ambiente, pois possuem seu comportamento voltado à dados consistentes e relevantes.\nCargos  e  Funções  relacionadas:  Geralmente é aplicado para funções que exijam a análise de dados para a transformação deles em informação que suporta a tomada de decisão. Pode ser utilizado para posições no financeiro, bussiness intelligence, análise de dados no geral, ou outras funções de backoffice como, por exemplo, RH, Marketing, Planejamento e TI.'
    ) as string,
    'Arquétipo Vendas': t(
      'Algoritmo que identifica indivíduos com alta capacidade de comunicação verbal e de persuasão para apresentar e convencer pessoas e grupos a tomar posições em linha com o que eles desejam. Pessoas com alto match neste algoritmo geralmente criam e mantém relacionamento com facilidade e criam conexões fortes com stakeholders. Possuem, também, habilidades fortes de automotivação na busca de padrões de excelência em suas atividades, grande habilidade em atuar em ambientes com ambiguidade e desafiam a zona de conforto e o status quo da organização, tomando a frente na condução de projetos e ações de alto impacto.\nCargos e Funções relacionadas: Inbound e Outbound Sales, Field Sales, SDR, Closer ou outras posições na área de vendas.'
    ) as string,
    'Arquétipo Atendimento': t(
      'Algoritmo que identifica indivíduos com alta habilidade de comunicação verbal e preferência por realizar atividades relativas à pessoas. Pessoas com match alto neste algoritmo possuem a capacidade de empatizar e de levar a opinião e o sentimento das outras pessoas em consideração nas interações e nas decisões, com uma compreensão assertiva das situações sociais. Conseguem facilmente interagir e criar relacionamento com pessoas diferentes, fornecendo suporte às pessoas em momentos e situações difíceis e garantindo a satisfação e coesão da equipe com o ambiente de trabalho. Geralmente possuem um alto grau de persuasão e grande capacidade de adaptação em ambientes diferentes de maneira sutil.\nCargos e Funções relacionadas: Consultor de atendimento, Customer Success, operadores e gestores de call center ou SAC, ou qualquer posição que precise lidar com suporte e atendimento ao cliente.'
    ) as string,
  };

  return (
    <ErrorResult error={jobPosting.error || functions.error}>
      <Helmet>
        <title>
          {t('Perfil')} | {jobPosting.data?.name || '...'} | {t('Recrutamento e Seleção')}
        </title>
      </Helmet>
      <PageContent>
        <Spin spinning={jobPosting.isLoading || functions.isLoading}>
          <Form<ProfileFormValues>
            form={form}
            layout="vertical"
            onFinish={(variables) =>
              mutation.mutate({
                ...variables,
                jobPostingId: id,
              })
            }
            initialValues={{
              functionId: jobPosting.data?.function_id || '',
            }}
            onValuesChange={() => setIsDirty(form.isFieldsTouched())}
          >
            <Form.Col span={10}>
              <Form.Item
                name="functionId"
                label={t('Escolha o arquétipo que melhor define o perfil da vaga')}
                required
                rules={[{ required: true, message: t('Por favor, insira um arquétipo') }]}
              >
                <Select>
                  {(
                    functions.data?.results ?? [
                      {
                        id: jobPosting.data?.function_id || 0,
                        name: '',
                        required_assessments: [],
                      },
                    ]
                  ).map((fn) => {
                    if (recruitmentProcesses.data?.count === 0 || !jobPosting.data?.function_id) {
                      return (
                        <Select.Option key={fn.id} value={fn.id}>
                          {getFrontendManualTranslations(
                            fn.name,
                            config.data?.t007_enableChangeLanguage || false
                          )}
                        </Select.Option>
                      );
                    }

                    const jobPostingFunction = functions.data?.results.find(
                      (fn) => fn.id === jobPosting.data.function_id
                    );

                    if (
                      fn.required_assessments?.length ===
                        jobPostingFunction?.required_assessments?.length &&
                      fn.required_assessments?.every((test) =>
                        jobPostingFunction?.required_assessments
                          ?.map((vacancyTest) => vacancyTest.key)
                          .includes(test.key)
                      )
                    ) {
                      return (
                        <Select.Option key={fn.id} value={fn.id}>
                          {fn.name}
                        </Select.Option>
                      );
                    }

                    return (
                      <Select.Option key={fn.id} value={fn.id} disabled>
                        <Tooltip
                          title={t(
                            'Não é possível alterar para um arquétipo com bateria de testes diferente, pois já existem candidatos na vaga'
                          )}
                          placement="right"
                        >
                          {fn.name}
                        </Tooltip>
                      </Select.Option>
                    );
                  })}
                </Select>
              </Form.Item>
            </Form.Col>
            <Space direction="vertical" size={16}>
              <Card>
                <Typography.Title level={5} style={{ fontWeight: 'normal' }}>
                  <Trans>Descrição</Trans>
                </Typography.Title>
                {functionName ? (
                  <MultilineParagraph>
                    {functionName in descriptions
                      ? descriptions[functionName]
                      : t(
                          'Este é um algoritmo personalizado para a sua empresa. Para mais detalhes contate o administrador do sistema.'
                        )}
                  </MultilineParagraph>
                ) : (
                  <Typography.Text type="secondary">
                    {t(
                      'Selecione um arquétipo para exibir sua descrição, incluindo pontos a serem avaliados, características e o perfil ideal para a vaga.'
                    )}
                  </Typography.Text>
                )}
                <Divider />
                <Typography.Title level={5} style={{ fontWeight: 'normal' }}>
                  <Trans>Testes Necessários</Trans>
                </Typography.Title>
                {functionName ? (
                  functions.data?.results
                    .find((fn) => fn.name === functionName)
                    ?.required_assessments?.map((test) => (
                      <React.Fragment key={test.key}>
                        {test.key === 'LOGIC' && (
                          <Tag icon={<BulbOutlined />}>
                            <Trans>Mental</Trans>
                          </Tag>
                        )}
                        {test.key === 'SJT' && (
                          <Tag icon={<MessageOutlined />}>
                            <Trans>Social</Trans>
                          </Tag>
                        )}
                        {test.key === 'OPERATIONAL' && (
                          <Tag icon={<NodeIndexOutlined />}>
                            <Trans>Raciocínio Simplificado</Trans>
                          </Tag>
                        )}
                        {test.key === 'PROFILEMAP' && (
                          <Tag icon={<UserOutlined />}>
                            <Trans>Perfil</Trans>
                          </Tag>
                        )}
                        {test.key === 'MOTIVATIONAL' && (
                          <Tag icon={<ThunderboltOutlined />}>
                            <Trans>Motivacional</Trans>
                          </Tag>
                        )}
                        {test.key === 'INTERESTS' && (
                          <Tag icon={<IdcardOutlined />}>
                            <Trans>Interesses profissionais</Trans>
                          </Tag>
                        )}
                      </React.Fragment>
                    ))
                ) : (
                  <Typography.Text type="secondary">
                    <Trans>
                      Selecione um arquétipo para exibir aqui os testes necessários para o perfil.
                    </Trans>
                  </Typography.Text>
                )}
              </Card>
              <Form.Footer>
                <Form.FooterEnd>
                  <Button
                    onClick={() =>
                      navigate({ pathname: '/:tenant/opportunities/:id/', params: { tenant, id } })
                    }
                  >
                    <Trans>Cancelar</Trans>
                  </Button>
                  <Button
                    htmlType="submit"
                    loading={mutation.isLoading}
                    onClick={() => {
                      searchParams.delete('new');
                      setLeavePage(true);
                    }}
                  >
                    <Trans>Salvar e Sair</Trans>
                  </Button>
                  <Button type="primary" htmlType="submit" loading={mutation.isLoading}>
                    {searchParams.get('new') ? (
                      <Trans>Salvar e continuar</Trans>
                    ) : (
                      <Trans>Salvar</Trans>
                    )}
                  </Button>
                </Form.FooterEnd>
              </Form.Footer>
            </Space>
          </Form>
          <div style={{ height: 80 }} />
        </Spin>
      </PageContent>
    </ErrorResult>
  );
};

export default withRouteChange()(ProfileForm);
